import { Editor as ToastEditor } from "@toast-ui/react-editor";
import Editor from "components/document/Create/Editor";
import DocumentTextField from "components/foundation/DocumentTextField/DocumentTextField";
import Select from "components/foundation/Select/Select";
import React, { useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import CreateDocumentForm from "../../CreateDocumentForm";
import CreateDocumentClassFormBody from "../CreateDocumentClassFormBody";
import useCompaniesQuery from "features/tag/hooks/queries/useCompaniesQuery";
import { TempDocumentDetailResponseWithAny } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import { LETTER_OF_APPROVAL_FIELD as FIELD } from "config/documentConstant";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";

/* 품의서 */
interface LetterOfApprovalForm {
  [FIELD.DOCUMENT_NUMBER.key]: string;
  [FIELD.TITLE.key]: string;
  [FIELD.CONTENT.key]: string;
}

interface LetterOfApprovalProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}

const LetterOfApproval = ({ tempDocumentDetail }: LetterOfApprovalProps) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<LetterOfApprovalForm>();
  const editorRef = useRef<ToastEditor | null>(null);

  useEffect(() => {
    if (editorRef.current && tempDocumentDetail) {
      editorRef.current
        .getInstance()
        .setHTML(tempDocumentDetail.documentData?.[FIELD.CONTENT.key]);
    }
  }, [tempDocumentDetail]);

  useEffect(() => {
    const editorInstance = editorRef.current;

    if (!editorInstance) return;

    const handleChange = () => {
      const value = editorInstance.getInstance().getHTML() || "";
      setValue(FIELD.CONTENT.key, value);
    };

    editorInstance.getInstance().on("change", handleChange);
    return () => editorInstance.getInstance().off("change");
  }, []);

  /* 법인 태그 */
  const { data: companyTagsData } = useCompaniesQuery();
  const companyTags = companyTagsData?.data.result;
  const companyList = companyTags?.map((tag) => ({
    label: tag.name,
    value: tag.uuid,
  }));

  return (
    <CreateDocumentClassFormBody noBorderTop>
      <CreateDocumentForm.Field label={FIELD.DOCUMENT_NUMBER.label}>
        <Controller
          name={FIELD.DOCUMENT_NUMBER.key}
          control={control}
          rules={{
            required: "법인명 선택 필수",
          }}
          render={({ field }) => (
            <Select
              placeholder="법인 선택"
              list={companyList}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
              }}
              errorMessage={errors[FIELD.DOCUMENT_NUMBER.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.TITLE.label}>
        <DocumentTextField
          {...register(FIELD.TITLE.key, {
            required: "제목 입력 필수",
            minLength: {
              value: FIELD.TITLE.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.TITLE.minLength,
                FIELD.TITLE.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.TITLE.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.TITLE.minLength,
                FIELD.TITLE.maxLength,
              ),
            },
          })}
          placeholder="제목을 입력해 주세요."
          errorMessage={errors[FIELD.TITLE.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.CONTENT.label}>
        <Controller
          name={FIELD.CONTENT.key}
          control={control}
          rules={{
            required: "내용 기입 필수",
          }}
          render={({ field }) => (
            <Editor
              ref={editorRef}
              placeholder="내용을 입력해 주세요."
              defaultValue={
                tempDocumentDetail?.documentData?.[FIELD.CONTENT.key] ||
                field.value
              }
              errorMessage={errors[FIELD.CONTENT.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>
    </CreateDocumentClassFormBody>
  );
};

export default LetterOfApproval;
